import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input } from '@cian/ui-kit';

import { useAvailableOptionGroups, useDefaultOptionGroups } from './hooks';
import { requestCurrentRegion } from '../../../actions/currentRegion';
import { closeLocationModal } from '../../../actions/locationModal';
import {
  IRegionOption,
  LocationModal,
  LocationModalContent,
  LocationModalHeader,
  LocationModalItem,
} from '../../../components/LocationModal/mobile';
import { selectIsLocationModalOpen } from '../../../selectors/locationModal';
import { selectRegions } from '../../../selectors/regions';
import { TThunkDispatch } from '../../../types/redux';
import { IRegion } from '../../../types/regions';
import { getRegionsById } from '../../../utils/regions';

export const LocationModalContainerMobile: React.FC = () => {
  const isOpen = useSelector(selectIsLocationModalOpen);
  const regions = useSelector(selectRegions);

  const [value, setValue] = React.useState('');

  const defaultOptionGroups = useDefaultOptionGroups();
  const optionGroups = useAvailableOptionGroups(defaultOptionGroups, regions, value);

  const regionsById = React.useMemo(() => getRegionsById(regions), [regions]);

  const contentRef = React.createRef<HTMLDivElement>();
  const dispatch: TThunkDispatch = useDispatch();

  const handleClose = React.useCallback(() => {
    dispatch(closeLocationModal());
  }, [dispatch]);

  const handleValueChange = React.useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      setValue(value);

      if (contentRef.current?.parentElement) {
        contentRef.current.parentElement.scrollTo(0, 0);
      }
    },
    [contentRef],
  );

  const handleRegionSelect = React.useCallback(
    (option: IRegionOption) => {
      dispatch(closeLocationModal());
      dispatch(requestCurrentRegion(regionsById.get(option.value) as IRegion));
    },
    [dispatch, regionsById],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <LocationModal
      title="Выберите город или регион"
      header={
        <LocationModalHeader>
          <Input
            size="M"
            placeholder="Например, Краснодарский край"
            value={value}
            onChange={handleValueChange}
            data-testid="LocationModalInput"
          />
        </LocationModalHeader>
      }
      onClose={handleClose}
    >
      <LocationModalContent ref={contentRef}>
        {optionGroups.map((group, groupIndex) => {
          return (
            <React.Fragment key={`optionsGroup_${groupIndex}`}>
              {group.title && <LocationModalItem bold>{group.title}</LocationModalItem>}
              <>
                {group.options.map(option => (
                  <LocationModalItem
                    key={option.value}
                    matches={option.matches}
                    onClick={() => handleRegionSelect(option)}
                  >
                    {option.title}
                  </LocationModalItem>
                ))}
              </>
            </React.Fragment>
          );
        })}
      </LocationModalContent>
    </LocationModal>
  );
};
